/*
  Scircle product templates (product.scircle, product.scircle-2).

  Scircle's integration script (loaded by snippets/scircle-aura.liquid, not forked) owns the chatbot iframe, the
  line item properties and the formula markup. This file owns what the theme needs around it:

  1. Page state. On `scircle` the whole page is hidden behind the chatbot until a formula exists
     (`is-scircle-pending` on <html>, styled by 13l-scircle.css) and revealed when Scircle posts `aura.formula`.
     The snippet already sets the pending class synchronously, so the work here is idempotent.
  2. Formula image in the gallery. When the product has media, the image Scircle renders in the info column is added
     to <product-gallery> as the last slide, after the product media, with its own thumbnail and dot
     (product-gallery.liquid renders the controls even for a single media on these templates). When the product has
     no media there is no gallery, so the image is copied into a `.scircle-media` container in the empty
     `product-gallery` grid area.
  3. Add-to-cart gate. Every submit of the product form (buy area and sticky bar, which targets the form through
     its `form` attribute) is locked until `properties[Formula ID]` exists in the form, so no order can be placed
     without a formula. Scircle only injects the properties once; if a partial rerender of the section drops them,
     they are re-created from the last formula received.
  4. Formula details. Scircle fills `#scircleScents-aura-formulaInfo` (description, tags, "Edit fragrance") once per
     formula; a partial rerender replaces the block with the empty server copy, so the filled nodes are moved into it.
  5. scircle-2: Scircle appends its fixed container to <body>; it is moved into <main> so 13l-scircle.css can keep
     it in flow above the product.

  Everything is re-queried on each mutation because <product-rerender allow-partial-rerender> replaces product-info
  blocks (and the sticky bar) when the form changes, so cached nodes go stale.
*/
(function () {
  'use strict';

  const SCIRCLE_ORIGIN = 'https://app.scircle.ai';
  const PENDING_CLASS = 'is-scircle-pending';
  const REVEALED_CLASS = 'is-scircle-revealed';
  const IMAGE_MOVED_CLASS = 'is-scircle-image-moved';
  const MEDIA_LOADED_CLASS = 'is-scircle-media-loaded';
  const GALLERY_READY_CLASS = 'is-scircle-gallery-ready';
  const SLIDE_MEDIA_ID = 'scircle-formula';
  const LOCKED_CLASS = 'scircle-atc-locked';
  const FORMULA_ID_NAME = 'properties[Formula ID]';
  const FORMULA_NAME_NAME = 'properties[Formula Name]';
  const FORMULA_CONTAINER_SELECTOR = '#scircleScents-aura-formulaInfo';
  const EDIT_BUTTON_SELECTOR = '#aura-edit-btn';
  const IFRAME_CONTAINER_ID = 'aura-iframe-container';

  const root = document.documentElement;
  const body = document.body;

  const mode = body.classList.contains('template-product-scircle')
    ? 'reveal'
    : body.classList.contains('template-product-scircle-2')
      ? 'inline'
      : null;

  if (!mode) return;

  const config = document.querySelector('[data-scircle-config]');
  const lockedLabel = (config && config.dataset.scircleLockedLabel) || 'Create your fragrance first';
  const imageAlt = (config && config.dataset.scircleImageAlt) || 'Your custom fragrance';

  const controller = new AbortController();
  const { signal } = controller;

  // Last formula received from Scircle, used to re-create the hidden inputs if a rerender drops them.
  let currentFormula = null;

  // Container Scircle last filled. A rerender (variant change) replaces it with the empty server copy.
  let filledContainer = null;

  /* ---------------------------------------------------------------------------------------------------------------
     Page state
     ------------------------------------------------------------------------------------------------------------- */

  function setPending() {
    root.classList.add(PENDING_CLASS);
    root.classList.remove(REVEALED_CLASS);
  }

  function setRevealed() {
    root.classList.remove(PENDING_CLASS);
    root.classList.add(REVEALED_CLASS);
  }

  if (mode === 'reveal') {
    setPending();
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Formula image -> gallery slot
     ------------------------------------------------------------------------------------------------------------- */

  function getFormulaContainer() {
    return document.querySelector(FORMULA_CONTAINER_SELECTOR);
  }

  function getMedia() {
    return document.querySelector('[data-scircle-media]');
  }

  // Scircle fills the container once per formula, so after a rerender its markup is moved into the new container.
  // The nodes are moved rather than cloned so the click listener Scircle bound to "Edit fragrance" survives.
  function syncFormulaInfo() {
    const container = getFormulaContainer();
    if (!container || !currentFormula) return;

    if (container.hasChildNodes()) {
      filledContainer = container;
      return;
    }

    if (!filledContainer || filledContainer === container || !filledContainer.hasChildNodes()) return;

    container.append(...filledContainer.childNodes);
    filledContainer = container;
  }

  // Scircle renders the formula image after it posts the formula: files.scircle.ai (imgix in front of Scircle's
  // renderer) holds each request for 3-6s and answers 500 until the render finishes (measured 10-30s, sometimes
  // longer), and Scircle's own <img> never retries. Ours retry almost at once, since the server already paces every
  // attempt, so the image shows right after the render lands instead of up to one interval later. They stay transparent
  // (13l-scircle.css) until they load, so the broken-image icon never shows. `wrapper` carries the loaded class.
  const IMAGE_RETRY_DELAYS = [250, 500, 1000];
  const IMAGE_RETRY_INTERVAL = 1500;
  const IMAGE_RETRY_LIMIT = 180000;
  const imageRetries = new WeakMap();

  function watchImage(image, wrapper) {
    image.addEventListener('load', () => wrapper.classList.add(MEDIA_LOADED_CLASS), { signal });
    image.addEventListener(
      'error',
      () => {
        const state = imageRetries.get(image);
        if (!state || Date.now() - state.startedAt > IMAGE_RETRY_LIMIT) return;

        const src = state.src;
        const delay = IMAGE_RETRY_DELAYS[state.attempt++] || IMAGE_RETRY_INTERVAL;
        state.timer = setTimeout(() => {
          if (image.getAttribute('src') !== src) return;
          image.removeAttribute('src');
          image.src = src;
        }, delay);
      },
      { signal }
    );
  }

  function stopImageRetries(root) {
    for (const image of root.querySelectorAll('img')) {
      const state = imageRetries.get(image);
      if (state) clearTimeout(state.timer);
      imageRetries.delete(image);
    }
  }

  function setImageSource(image, wrapper, src) {
    if (image.getAttribute('src') === src) return;

    const previous = imageRetries.get(image);
    if (previous) clearTimeout(previous.timer);
    imageRetries.set(image, { src, attempt: 0, timer: 0, startedAt: Date.now() });

    wrapper.classList.remove(MEDIA_LOADED_CLASS);
    image.src = src;

    // A cached image can be complete before the load listener fires.
    if (image.complete && image.naturalWidth > 0) wrapper.classList.add(MEDIA_LOADED_CLASS);
  }

  function getGallery() {
    return document.querySelector('.shopify-section--main-product product-gallery[data-scircle-gallery]');
  }

  function getSlide(gallery) {
    return gallery.querySelector(`.product-gallery__media[data-media-id="${SLIDE_MEDIA_ID}"]`);
  }

  function formatLabel(template, values) {
    return (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  // <carousel-navigation> and <product-gallery-navigation> collect their buttons once, in connectedCallback, so a
  // button added or removed later is ignored until the element is connected again.
  function reconnect(element) {
    const { parentNode, nextSibling } = element;
    element.remove();
    parentNode.insertBefore(element, nextSibling);
  }

  // Liquid numbers slides ("Item n of count") against the product media only; the formula slide adds one more, so every
  // count is recounted and the new controls get their "Go to item n".
  function relabelSlides(gallery) {
    const slides = gallery.querySelectorAll('.product-gallery__carousel > .product-gallery__media');
    slides.forEach((slide, index) => {
      slide.setAttribute(
        'aria-label',
        formatLabel(gallery.dataset.scircleItemLabel, { index: index + 1, count: slides.length })
      );
    });

    const goToTemplate = gallery.dataset.scircleGoToLabel;
    if (!goToTemplate) return;

    gallery.querySelectorAll('product-gallery-navigation .product-gallery__thumbnail').forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-label', formatLabel(goToTemplate, { index: index + 1 }));
    });

    gallery.querySelectorAll('carousel-navigation button').forEach((dot, index) => {
      const label = dot.querySelector('.sr-only');
      if (label) label.textContent = formatLabel(goToTemplate, { index: index + 1 });
    });
  }

  function createGallerySlide(gallery) {
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return null;

    const slide = document.createElement('div');
    slide.className = 'product-gallery__media snap-center';
    slide.setAttribute('data-media-type', 'image');
    slide.setAttribute('data-media-id', SLIDE_MEDIA_ID);
    slide.setAttribute('role', 'group');

    // A direct <img> child with width/height, like the media snippet renders: the lightbox reads exactly that.
    const image = document.createElement('img');
    image.alt = imageAlt;
    image.width = 1000;
    image.height = 1000;
    watchImage(image, slide);
    slide.appendChild(image);
    // Appended after the product media: product images open the gallery and every Prestige index (`carousel.filter()`
    // on variant media, `featured_media.position - 1` on variant change) still matches the Liquid position.
    carousel.append(slide);

    const slideCount = carousel.querySelectorAll(':scope > .product-gallery__media').length;
    const goToLabel = formatLabel(gallery.dataset.scircleGoToLabel, { index: slideCount });

    const thumbnails = gallery.querySelector('product-gallery-navigation');
    if (thumbnails) {
      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'product-gallery__thumbnail';
      thumbnail.setAttribute('data-media-type', 'image');
      // Product thumbnails are numbered from 1; the last position keeps the order <product-gallery-navigation>
      // compares on scroll.
      thumbnail.setAttribute('data-media-position', String(slideCount));
      thumbnail.setAttribute('data-media-id', SLIDE_MEDIA_ID);
      thumbnail.setAttribute('data-scircle-slide-control', '');
      thumbnail.setAttribute('aria-current', 'false');
      thumbnail.setAttribute('aria-label', goToLabel);

      const thumbnailImage = document.createElement('img');
      thumbnailImage.className = 'object-contain';
      thumbnailImage.alt = '';
      thumbnailImage.width = 110;
      thumbnailImage.height = 110;
      watchImage(thumbnailImage, thumbnail);
      thumbnail.appendChild(thumbnailImage);

      thumbnails.append(thumbnail);
      reconnect(thumbnails);
    }

    const dots = gallery.querySelector('carousel-navigation');
    if (dots) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tap-area';
      dot.setAttribute('data-scircle-slide-control', '');
      dot.setAttribute('aria-current', 'false');

      const dotLabel = document.createElement('span');
      dotLabel.className = 'sr-only';
      dotLabel.textContent = goToLabel;
      dot.appendChild(dotLabel);

      dots.append(dot);
      reconnect(dots);
    }

    relabelSlides(gallery);
    return slide;
  }

  // Used when the formula slide is removed while it may be the selected one. Selecting index 0 when the carousel was
  // already at 0 fires no `carousel:change`, so the thumbnails and dots are told directly.
  function selectFirstSlide(gallery) {
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel || typeof carousel.select !== 'function') return;

    carousel.select(0, { instant: true });
    for (const navigation of gallery.querySelectorAll('product-gallery-navigation, carousel-navigation')) {
      if (typeof navigation.onNavigationChange === 'function') navigation.onNavigationChange(0);
    }
  }

  function syncGallerySlide(gallery, src) {
    let slide = getSlide(gallery);

    if (!slide) {
      slide = createGallerySlide(gallery);
      if (!slide) return;
    }

    // Scircle may post `aura.formula` more than once for the same formula (the first time before it has resolved
    // the image version), so the src can legitimately change after the first render.
    setImageSource(slide.querySelector('img'), slide, src);

    // The thumbnail uses the slide's exact URL on purpose. Every `w` is a separate imgix derivative that fetches the
    // render from Scircle on its own, so a smaller thumbnail URL doubled the requests while the render was still
    // pending and arrived 7-10s after the slide. The same URL is one request, shared by both <img> (and by Scircle's).
    const thumbnail = gallery.querySelector(`.product-gallery__thumbnail[data-media-id="${SLIDE_MEDIA_ID}"]`);
    if (thumbnail) setImageSource(thumbnail.querySelector('img'), thumbnail, src);

    gallery.classList.add(GALLERY_READY_CLASS);
  }

  function removeGallerySlide() {
    const gallery = getGallery();
    if (!gallery) return;

    const slide = getSlide(gallery);
    if (!slide) return;

    stopImageRetries(slide);
    slide.remove();
    for (const control of gallery.querySelectorAll('[data-scircle-slide-control]')) {
      stopImageRetries(control);
      control.remove();
    }
    for (const navigation of gallery.querySelectorAll('product-gallery-navigation, carousel-navigation')) {
      reconnect(navigation);
    }

    gallery.classList.remove(GALLERY_READY_CLASS);
    relabelSlides(gallery);
    selectFirstSlide(gallery);
  }

  function removeMedia() {
    const media = getMedia();
    if (media) {
      stopImageRetries(media);
      media.remove();
    }
    removeGallerySlide();

    const container = getFormulaContainer();
    if (container) container.classList.remove(IMAGE_MOVED_CLASS);
  }

  function syncFormulaImage() {
    const container = getFormulaContainer();
    if (!container) return;

    const sourceImage = container.querySelector('img.aura-formula-image');
    if (!sourceImage || !sourceImage.getAttribute('src')) {
      return;
    }

    const gallery = getGallery();
    if (gallery) {
      syncGallerySlide(gallery, sourceImage.getAttribute('src'));
      container.classList.add(IMAGE_MOVED_CLASS);
      return;
    }

    const product = document.querySelector('.shopify-section--main-product .product');
    if (!product) return;

    let media = getMedia();
    let image = media ? media.querySelector('.scircle-media__image') : null;

    if (!media || !image) {
      media = document.createElement('div');
      media.className = 'scircle-media';
      media.setAttribute('data-scircle-media', '');

      image = document.createElement('img');
      image.className = 'scircle-media__image';
      image.alt = imageAlt;
      image.width = 1000;
      image.height = 1000;
      image.loading = 'eager';
      watchImage(image, media);

      media.appendChild(image);
    }

    // A rerender may have replaced `.product`; keep the media as its first child.
    if (media.parentNode !== product || product.firstElementChild !== media) {
      product.insertBefore(media, product.firstChild);
    }

    setImageSource(image, media, sourceImage.getAttribute('src'));

    container.classList.add(IMAGE_MOVED_CLASS);
  }

  // Scircle renders "Edit fragrance" as a <span> with a click handler: not reachable by keyboard on its own.
  function syncEditButton() {
    const container = getFormulaContainer();
    if (!container) return;

    const button = container.querySelector(EDIT_BUTTON_SELECTOR);
    if (!button || button.getAttribute('role') === 'button') return;

    button.setAttribute('role', 'button');
    button.setAttribute('tabindex', '0');
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Add-to-cart gate
     ------------------------------------------------------------------------------------------------------------- */

  function getProductForms() {
    return document.querySelectorAll('.shopify-section--main-product form[action*="/cart/add"]');
  }

  function getSubmitButtons(form) {
    const buttons = new Set(form.querySelectorAll('button[name="add"]'));

    // `form.id` is shadowed by the variant `<input name="id">` (named form controls become properties of the form
    // element), so the attribute is the only reliable way to read the id.
    const formId = form.getAttribute('id');
    if (formId) {
      // The sticky bar lives outside the form and points at it through the `form` attribute. Its button carries no
      // explicit type (a <button> submits by default), so match on the form attribute alone and skip type="button".
      const external = document.querySelectorAll(`button[form="${formId}"]:not([type="button"])`);
      for (const button of external) buttons.add(button);
    }

    return buttons;
  }

  function hasFormula(form) {
    const input = form.querySelector(`input[name="${FORMULA_ID_NAME}"]`);
    return Boolean(input && input.value);
  }

  function restoreFormulaInputs(form) {
    if (!currentFormula || !currentFormula.uuid) return;

    for (const [name, value] of [
      [FORMULA_ID_NAME, currentFormula.uuid],
      [FORMULA_NAME_NAME, currentFormula.name]
    ]) {
      if (!value || form.querySelector(`input[name="${name}"]`)) continue;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.className = 'aura-data-input';
      form.appendChild(input);
    }
  }

  function setButtonLabel(button, label) {
    // The button content is either a text node or `.text-with-icon` wrapping icon + text; swap the text only.
    const target = button.querySelector('.text-with-icon') || button;
    for (const node of target.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
        node.textContent = label;
        return;
      }
    }
    target.appendChild(document.createTextNode(label));
  }

  function lockButton(button) {
    if (button.classList.contains(LOCKED_CLASS)) return;

    // A button the theme disabled on its own (sold out, unavailable) is left alone.
    if (button.disabled) return;

    button.dataset.scircleOriginalLabel = button.textContent.trim();
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add(LOCKED_CLASS);
    setButtonLabel(button, lockedLabel);
  }

  function unlockButton(button) {
    if (!button.classList.contains(LOCKED_CLASS)) return;

    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.classList.remove(LOCKED_CLASS);

    const original = button.dataset.scircleOriginalLabel;
    if (original) setButtonLabel(button, original);
    delete button.dataset.scircleOriginalLabel;
  }

  function syncAtcGate() {
    for (const form of getProductForms()) {
      if (!hasFormula(form)) restoreFormulaInputs(form);

      const unlocked = hasFormula(form);
      for (const button of getSubmitButtons(form)) {
        if (unlocked) {
          unlockButton(button);
        } else {
          lockButton(button);
        }
      }
    }
  }

  function clearFormulaInputs() {
    for (const form of getProductForms()) {
      for (const input of form.querySelectorAll(`input[name="${FORMULA_ID_NAME}"], input[name="${FORMULA_NAME_NAME}"]`)) {
        input.remove();
      }
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     scircle-2: iframe container into <main>
     ------------------------------------------------------------------------------------------------------------- */

  function moveIframeContainerIntoMain() {
    const container = document.getElementById(IFRAME_CONTAINER_ID);
    const main = document.getElementById('main') || document.querySelector('main');
    if (!container || !main) return false;

    if (main.firstElementChild !== container) {
      main.insertBefore(container, main.firstChild);
    }
    return true;
  }

  if (mode === 'inline') {
    if (!moveIframeContainerIntoMain()) {
      // Scircle appends the container to <body> once it finds the header; watch body's direct children only.
      const bodyObserver = new MutationObserver(() => {
        if (moveIframeContainerIntoMain()) bodyObserver.disconnect();
      });
      bodyObserver.observe(body, { childList: true });
      signal.addEventListener('abort', () => bodyObserver.disconnect(), { once: true });
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Wiring
     ------------------------------------------------------------------------------------------------------------- */

  function syncAll() {
    syncFormulaInfo();
    syncFormulaImage();
    syncEditButton();
    syncAtcGate();
  }

  window.addEventListener(
    'message',
    (event) => {
      if (event.origin !== SCIRCLE_ORIGIN) return;

      const data = event.data;
      if (!data || data.type !== 'aura.formula' || !data.data) return;

      currentFormula = {
        uuid: typeof data.data.uuid === 'string' ? data.data.uuid : '',
        name: typeof data.data.name === 'string' ? data.data.name : ''
      };

      if (mode === 'reveal') {
        setRevealed();
        window.scrollTo({ top: 0 });
      }

      // Scircle injects the inputs after an async fetch; the observer below unlocks the ATC when they land.
      syncAll();
    },
    { signal }
  );

  function handleEdit() {
    currentFormula = null;
    filledContainer = null;
    removeMedia();
    clearFormulaInputs();
    syncAtcGate();

    if (mode === 'reveal') {
      setPending();
      window.scrollTo({ top: 0 });
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(EDIT_BUTTON_SELECTOR) : null;
      if (button) handleEdit();
    },
    { signal }
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const button = event.target instanceof Element ? event.target.closest(EDIT_BUTTON_SELECTOR) : null;
      if (!button) return;

      event.preventDefault();
      // Scircle's own click handler (title restore, iframe re-show) runs through the synthetic click.
      button.click();
    },
    { signal }
  );

  // The main product section is the narrowest common ancestor of the form, the sticky bar, the formula container
  // and `.product`, and it is where <product-rerender> swaps markup.
  const section = document.querySelector('.shopify-section--main-product');
  if (section) {
    const sectionObserver = new MutationObserver(syncAll);
    sectionObserver.observe(section, { childList: true, subtree: true });
    signal.addEventListener('abort', () => sectionObserver.disconnect(), { once: true });
  }

  window.addEventListener('pagehide', () => controller.abort(), { once: true });

  syncAll();
})();
